import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export function DateRangePicker({ 
  dateRange, 
  onDateRangeChange, 
  className,
  placeholder = "Select date range"
}) {
  const [open, setOpen] = React.useState(false)

  const handleSelect = (range) => {
    onDateRangeChange(range)
    if (range?.from && range?.to) {
      setOpen(false)
    }
  }

  const handleClear = (e) => {
    // Radix PopoverTrigger opens on pointerdown — plain onClick fires
    // too late so the popover flashes open and dateRange never clears.
    // Stop pointerdown at capture phase and close explicitly.
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    onDateRangeChange({ from: undefined, to: undefined })
    setOpen(false)
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[280px] justify-start text-left font-normal",
              !dateRange?.from && "text-muted-foreground"
            )}
            data-testid="date-range-picker"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "dd MMM yyyy")} -{" "}
                  {format(dateRange.to, "dd MMM yyyy")}
                </>
              ) : (
                format(dateRange.from, "dd MMM yyyy")
              )
            ) : (
              <span>{placeholder}</span>
            )}
            {(dateRange?.from || dateRange?.to) && (
              <span
                role="button"
                aria-label="Clear date range"
                tabIndex={0}
                onPointerDownCapture={handleClear}
                onClick={handleClear}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e) }}
                className="ml-auto inline-flex items-center justify-center rounded p-0.5 opacity-50 hover:opacity-100 hover:bg-muted cursor-pointer"
                data-testid="date-range-clear-btn"
              >
                <X className="h-4 w-4" />
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={handleSelect}
            numberOfMonths={2}
          />
          <div className="flex gap-2 p-3 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => {
                const today = new Date()
                const weekAgo = new Date(today)
                weekAgo.setDate(weekAgo.getDate() - 7)
                handleSelect({ from: weekAgo, to: today })
              }}
            >
              Last 7 days
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="flex-1"
              onClick={() => {
                const today = new Date()
                const monthAgo = new Date(today)
                monthAgo.setDate(monthAgo.getDate() - 30)
                handleSelect({ from: monthAgo, to: today })
              }}
            >
              Last 30 days
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="flex-1"
              onClick={() => {
                const today = new Date()
                const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
                handleSelect({ from: startOfMonth, to: today })
              }}
            >
              This month
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
